import { useState } from 'react';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import classnames from 'classnames';
import SoundEffectCategories from '@App/api/query/SoundEffectCategories.graphql';
import Query from '@App/components/api/Query';
import Button from '@App/components/ui/Button';

const CategoryPropType = PropTypes.shape({
  name: PropTypes.string.isRequired,
  subCategories: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired).isRequired,
});

interface SubCategory {
  id: string
  name: string
}

interface Category {
  name: string
  subCategories: SubCategory[]
}

interface Props {
  categories: Category[]
  loading: boolean
  selectedCatId?: string | null
  onSelect: (id: string, label: string) => void
  onClear: () => void
}

/**
 * Category filter column for the sound effects page (/effets-sonores).
 *
 * Mirrors the music library's `Filters` (`ul.filters__column`): a narrow level-1 column of UCS
 * categories; clicking one opens a wider level-2 column of its sub-categories. Selecting a
 * sub-category — or "the whole category" — sets the active `ucsCatId` filter on the page, which
 * re-runs the search (browsing works even with no text term). The taxonomy comes from the API
 * (`soundEffectCategories`), not hard-coded.
 *
 * The column carries no "all categories" entry, no more than the music one does: nothing selected IS
 * the whole catalogue, and the active criterion is dropped from the chip in the search bar or by
 * clicking it again here.
 */
// Exported (named) for isolated component testing: the default export wraps this presentational
// column with the Apollo `Query` that fetches the taxonomy; tests mount this pure part directly.
export function SoundEffectFilters({ categories, loading, selectedCatId, onSelect, onClear }: Props) {
  const [openCategory, setOpenCategory] = useState<string | null>(null);

  const onToggleCategory = (event) => {
    const name = event.currentTarget.value;
    setOpenCategory(prev => (prev === name ? null : name));
  };

  // Clicking the active sub-category again clears the filter. It is the only way back to the whole
  // catalogue on mobile, where the chip carrying the criterion in the search bar is hidden.
  const onPick = (id: string, label: string) => {
    if (id === selectedCatId) {
      onClear();

      return;
    }

    onSelect(id, label);
  };

  if (loading) {
    return <ul className="filters__column loading" />;
  }

  const active = categories.find(category => category.name === openCategory) || null;

  return (
    <>
      <ul className="filters__column">
        {categories.map(({ name }) => (
          <li key={name} className={classnames('filters__item', { active: openCategory === name })}>
            <Button label={name} value={name} onClick={onToggleCategory} className="" />
          </li>
        ))}
      </ul>
      {active ? (
        <ul className="filters__column filters__column--large">
          <li className="filters__item">
            <Button
              label={I18n.t('soundEffects.filters.allInCategory', { category: active.name })}
              onClick={() => onPick(active.subCategories.map(sub => sub.id).join(','), active.name)}
              className=""
            />
          </li>
          {active.subCategories.map(sub => (
            <li key={sub.id} className={classnames('filters__item', { active: selectedCatId === sub.id })}>
              <Button
                label={sub.name}
                onClick={() => onPick(sub.id, `${active.name} · ${sub.name}`)}
                className=""
              />
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

SoundEffectFilters.propTypes = {
  categories: PropTypes.arrayOf(CategoryPropType.isRequired).isRequired,
  loading: PropTypes.bool.isRequired,
  selectedCatId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

SoundEffectFilters.defaultProps = {
  selectedCatId: null,
};

function mapResult(data) {
  return { categories: data.soundEffectCategories, loading: false };
}

function mapLoading() {
  return { categories: [], loading: true };
}

interface WrapperProps {
  selectedCatId?: string | null
  onSelect: (id: string, label: string) => void
  onClear: () => void
}

export default function SoundEffectFiltersWrapper(props: WrapperProps) {
  return (
    <Query
      component={SoundEffectFilters}
      loadingComponent={SoundEffectFilters}
      query={SoundEffectCategories}
      mapResult={mapResult}
      mapLoading={mapLoading}
      childProps={props}
    />
  );
}

SoundEffectFiltersWrapper.propTypes = {
  selectedCatId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};
